import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TileEmpty } from '../TileWrapper';
import { MapPin, LogIn, LogOut, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_SITES = [
  { id: '1', name: 'Site Alpha - Main Building' },
  { id: '2', name: 'Warehouse B' },
  { id: '3', name: 'Distribution Center' },
  { id: '4', name: 'Head Office' },
];

const MOCK_CHECKIN = {
  id: '1',
  site_id: '1',
  check_in_time: new Date(Date.now() - 7200000).toISOString(),
  site_locations: { name: 'Site Alpha - Main Building' },
};

export function QuickCheckInTile({ filter, size, useMockData }) {
  const { userProfile, company } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [currentCheckIn, setCurrentCheckIn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (useMockData) {
      setSites(MOCK_SITES);
      setCurrentCheckIn(MOCK_CHECKIN);
      setSelectedSite(MOCK_CHECKIN.site_id);
      setLoading(false);
      return;
    }

    if (!userProfile?.id || !company?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        // Get available sites
        const { data: siteData, error: siteError } = await supabase
          .from('site_locations')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_active', true)
          .order('name');

        if (siteError) throw siteError;

        setSites(siteData || []);

        // Check if user is currently checked in
        const { data: checkIn, error: checkInError } = await supabase
          .from('site_checkins')
          .select('*, site_locations(name)')
          .eq('user_id', userProfile.id)
          .is('check_out_time', null)
          .order('check_in_time', { ascending: false })
          .limit(1)
          .single();

        if (!checkInError && checkIn) {
          setCurrentCheckIn(checkIn);
          setSelectedSite(checkIn.site_id);
        } else if (siteData?.length > 0) {
          setSelectedSite(siteData[0].id);
        }
      } catch (err) {
        console.error('Failed to load check-in data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.id, company?.id, useMockData]);

  const handleCheckIn = async () => {
    if (!selectedSite || !userProfile?.id) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from('site_checkins')
        .insert({
          site_id: selectedSite,
          user_id: userProfile.id,
          check_in_time: new Date().toISOString(),
        })
        .select('*, site_locations(name)')
        .single();

      if (error) throw error;

      setCurrentCheckIn(data);
      toast.success(`Checked in to ${data.site_locations.name}`);
    } catch (err) {
      console.error('Check-in failed:', err);
      toast.error('Failed to check in');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckOut = async () => {
    if (!currentCheckIn?.id) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('site_checkins')
        .update({ check_out_time: new Date().toISOString() })
        .eq('id', currentCheckIn.id);

      if (error) throw error;

      toast.success('Checked out successfully');
      setCurrentCheckIn(null);
    } catch (err) {
      console.error('Check-out failed:', err);
      toast.error('Failed to check out');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (sites.length === 0) {
    return <TileEmpty message="No sites available" icon={MapPin} />;
  }

  const isCheckedIn = !!currentCheckIn;
  const duration = currentCheckIn?.check_in_time
    ? formatDistanceToNow(new Date(currentCheckIn.check_in_time))
    : null;

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-2">
      {isCheckedIn ? (
        <>
          <div className="bg-green-100 p-3 rounded-full mb-3">
            <MapPin className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-sm font-medium text-green-700 mb-1">Checked In</p>
          <p className="text-xs text-gray-600 mb-1">
            {currentCheckIn.site_locations?.name}
          </p>
          <p className="text-xs text-gray-400 flex items-center gap-1 mb-3">
            <Clock className="h-3 w-3" />
            {duration}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCheckOut}
            disabled={submitting}
            className="w-full"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <LogOut className="h-4 w-4 mr-1" />
                Check Out
              </>
            )}
          </Button>
        </>
      ) : (
        <>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-full mb-3 text-xs h-8">
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={handleCheckIn}
            disabled={submitting || !selectedSite}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <LogIn className="h-4 w-4 mr-1" />
                Check In
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
